import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectIsAuthenticated, restoreSession, sessionExpired } from '@/features/auth';
import { selectTheme } from '@/features/settings';
import { store } from '@/app/store';

function ThemeWatcher(): null {
  const theme = useAppSelector(selectTheme);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  return null;
}

function SessionWatcher(): null {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  // Ref survives React 18 StrictMode's unmount/remount cycle — prevents double dispatch.
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasRestoredRef.current) {
      hasRestoredRef.current = true;
      void dispatch(restoreSession());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once on mount
  }, [dispatch]);

  useEffect(() => {
    const handler = (): void => {
      dispatch(sessionExpired());
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [dispatch]);
  return null;
}

export function AppProviders({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: 0, // Always consider data stale to force fresh API calls
            gcTime: 0, // Do not keep unused data in cache — discard immediately
            refetchOnWindowFocus: true, // Refetch when user returns to the tab
            refetchOnMount: 'always', // Always refetch when component mounts
          },
        },
      }),
  );

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeWatcher />
        <SessionWatcher />
        {children}
        <Toaster richColors position="top-right" />
      </QueryClientProvider>
    </Provider>
  );
}
